import latinize from 'latinize';
import {from, type Observable} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';

import {getApolloClient} from 'web-app/api/clients/famly-app';

import type {PersistedAudio} from '../types';
import type {
    CreateAudioUploadUrlMutation,
    CreateAudioUploadUrlMutationVariables,
    RegisterAudioUploadMutation,
    RegisterAudioUploadMutationVariables,
} from './__generated__/queries.api-types';
import {CreateAudioUploadUrl, RegisterAudioUpload} from './queries';
import {getCleanedFileName} from './utils';

type AudioUpload = {
    signedUploadUrl: string;
    jwt: string;
};

export const registerAudioFile = (audioUpload: AudioUpload, filename: string): Promise<PersistedAudio | undefined> => {
    const client = getApolloClient();

    return client
        .mutate<RegisterAudioUploadMutation, RegisterAudioUploadMutationVariables>({
            mutation: RegisterAudioUpload,
            variables: {jwt: audioUpload.jwt, filename: getCleanedFileName(latinize(filename))},
        })
        .then(fetchResult => {
            const audio = fetchResult.data?.audioFiles.registerAudio;

            if (!audio) {
                return undefined;
            }

            return {
                id: audio.id,
                fileId: audio.id,
                mediaType: 'Audio',
                temporaryFileUrl: audio.signedUrl ?? '',
            };
        });
};

export const createAudioUploadUrl = (filename: string): Promise<AudioUpload | undefined> => {
    const client = getApolloClient();

    return client
        .mutate<CreateAudioUploadUrlMutation, CreateAudioUploadUrlMutationVariables>({
            mutation: CreateAudioUploadUrl,
            variables: {filename: getCleanedFileName(latinize(filename))},
        })
        .then(fetchResult => {
            const signedUploadUrl = fetchResult.data?.audioFiles.createUploadUrl.signedUploadUrl;
            const jwt = fetchResult.data?.audioFiles.createUploadUrl.jwt;

            if (!signedUploadUrl || !jwt) {
                return undefined;
            }

            return {signedUploadUrl, jwt};
        });
};

export async function uploadAudioFile(
    data: AudioUpload,
    file?: File,
    onProgress?: (progress: number) => void,
): Promise<AudioUpload> {
    return new Promise((res, rej) => {
        const xhttp = new XMLHttpRequest();
        xhttp.onerror = () => {
            rej();
        };
        xhttp.onreadystatechange = () => {
            if (xhttp.readyState === 4) {
                res(data);
            }
        };

        xhttp.upload.onprogress = event => {
            if (event.lengthComputable && onProgress) {
                const percentComplete = Math.round((event.loaded / event.total) * 100);
                onProgress(percentComplete);
            }
        };

        if (data.signedUploadUrl && file) {
            xhttp.open('PUT', data.signedUploadUrl);
            xhttp.setRequestHeader('Content-Type', file.type);
            xhttp.send(file);
        }
    });
}

/**
 * Combined observable that first creates an audio upload URL and then uploads the file
 *
 * @param {File} file - Audio file to upload
 * @param {(progress: number) => void} [onProgress] - Optional callback for progress updates
 *
 * @returns Observable<FileUpload> - an observable of the uploaded file details
 */
export const uploadAudioFileObservable = (
    file: File,
    onProgress?: (progress: number) => void,
): Observable<AudioUpload> => {
    return from(createAudioUploadUrl(file.name)).pipe(
        mergeMap(uploadUrlData => {
            if (!uploadUrlData) {
                throw new Error('Failed to create audio upload URL');
            }

            return from(uploadAudioFile(uploadUrlData, file, onProgress));
        }),
        map(fileUpload => fileUpload),
    );
};
